import React, { useState } from "react";
import axios from 'axios';
import { useSuperAdminAuth } from '../../../context/SuperAdminAuthContext';
import SEO from "../../../components/common/SEO";

function SuperAdminLogin() {
    const { login } = useSuperAdminAuth();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [formMessage, setFormMessage] = useState({ text: '', type: '' });

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setFormMessage({ text: '', type: '' });

        if (!email || !password) {
            setFormMessage({ text: "Please enter email and password.", type: "error" });
            setIsLoading(false);
            return;
        }

        try {
            const apiUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/superadmin/login`;
            const response = await axios.post(apiUrl, { email, password });
            const { admin, token, message } = response.data;

            setFormMessage({ text: message || "Login successful!", type: "success" });
            setTimeout(() => {
                login(token, admin);
            }, 1000);
        } catch (err) {
            const errorMessage = err.response?.data?.message || "Login failed. Please check your credentials.";
            setFormMessage({ text: errorMessage, type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="superadmin-auth-container">
            <style>{`
                .superadmin-auth-container * {
                    box-sizing: border-box;
                }
                .superadmin-auth-container {
                    min-height: 100vh;
                    width: 100vw;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8fafc;
                    background-image: 
                        radial-gradient(at 40% 20%, hsla(228,100%,74%,0.15) 0px, transparent 50%),
                        radial-gradient(at 80% 0%, hsla(189,100%,56%,0.15) 0px, transparent 50%),
                        radial-gradient(at 0% 50%, hsla(355,100%,93%,0.15) 0px, transparent 50%),
                        radial-gradient(at 80% 50%, hsla(340,100%,76%,0.15) 0px, transparent 50%),
                        radial-gradient(at 0% 100%, hsla(22,100%,77%,0.15) 0px, transparent 50%),
                        radial-gradient(at 80% 100%, hsla(242,100%,70%,0.15) 0px, transparent 50%),
                        radial-gradient(at 0% 0%, hsla(343,100%,76%,0.15) 0px, transparent 50%);
                    font-family: 'Inter', 'Poppins', sans-serif;
                    padding: 20px;
                    position: relative;
                    overflow: hidden;
                }
                .superadmin-auth-card {
                    background: rgba(255, 255, 255, 0.7);
                    backdrop-filter: blur(24px);
                    -webkit-backdrop-filter: blur(24px);
                    border: 1px solid rgba(255, 255, 255, 0.5);
                    border-radius: 28px;
                    padding: 48px;
                    width: 100%;
                    max-width: 440px;
                    z-index: 1;
                    box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.05), 0 0 20px rgba(255, 255, 255, 0.5) inset;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes slideUp {
                    0% { opacity: 0; transform: translateY(30px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                .superadmin-logo {
                    height: 48px;
                    margin-bottom: 24px;
                    object-fit: contain;
                    animation: pulse 3s infinite ease-in-out alternate;
                }
                @keyframes pulse {
                    0% { transform: scale(1); }
                    100% { transform: scale(1.02); }
                }
                .superadmin-heading {
                    color: #0f172a;
                    font-size: 26px;
                    font-weight: 700;
                    margin: 0 0 8px 0;
                    text-align: center;
                    letter-spacing: -0.5px;
                }
                .superadmin-subheading {
                    color: #64748b;
                    font-size: 15px;
                    margin: 0 0 36px 0;
                    text-align: center;
                }
                .superadmin-input-group {
                    width: 100%;
                    margin-bottom: 20px;
                    position: relative;
                }
                .superadmin-input-group label {
                    display: block;
                    color: #334155;
                    font-size: 14px;
                    font-weight: 600;
                    margin-bottom: 8px;
                }
                .superadmin-input {
                    width: 100%;
                    background: rgba(255, 255, 255, 0.8);
                    border: 1px solid rgba(226, 232, 240, 0.8);
                    border-radius: 14px;
                    padding: 14px 18px;
                    color: #0f172a;
                    font-size: 15px;
                    transition: all 0.3s ease;
                    outline: none;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02) inset;
                }
                .superadmin-input:focus {
                    border-color: #3b82f6;
                    background: #ffffff;
                    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
                    transform: translateY(-1px);
                }
                .superadmin-btn {
                    width: 100%;
                    background: linear-gradient(135deg, #2563eb 0%, #4f46e5 100%);
                    color: #ffffff;
                    border: none;
                    border-radius: 14px;
                    padding: 16px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    margin-top: 12px;
                    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.3);
                }
                .superadmin-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.4);
                    background: linear-gradient(135deg, #1d4ed8 0%, #4338ca 100%);
                }
                .superadmin-btn:active {
                    transform: translateY(0);
                }
                .superadmin-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }
                .superadmin-message {
                    width: 100%;
                    padding: 14px 16px;
                    border-radius: 12px;
                    margin-bottom: 24px;
                    font-size: 14px;
                    text-align: center;
                    font-weight: 500;
                    animation: fadeIn 0.3s ease;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .superadmin-message.error {
                    background: #fef2f2;
                    color: #dc2626;
                    border: 1px solid #fecaca;
                }
                .superadmin-message.success {
                    background: #f0fdf4;
                    color: #16a34a;
                    border: 1px solid #bbf7d0;
                }
                @media (max-width: 640px) {
                    .superadmin-auth-card {
                        padding: 40px 24px;
                        border-radius: 24px;
                    }
                    .superadmin-heading {
                        font-size: 24px;
                    }
                }
            `}</style>

            <SEO
                title="Super Admin Login | Mangoloops"
                description="Secure login portal for Mangoloops Super Administrators."
            />

            <div className="superadmin-auth-card">
                <img
                    src="/logo-mangoloops.png"
                    alt="Mangoloops Logo"
                    className="superadmin-logo"
                />

                <h2 className="superadmin-heading">Access Super Admin Portal</h2>
                <p className="superadmin-subheading">Secure authentication required</p>

                {formMessage.text && (
                    <div className={`superadmin-message ${formMessage.type}`}>
                        {formMessage.text}
                    </div>
                )}

                <form onSubmit={handleLogin} style={{ width: '100%' }}>
                    <div className="superadmin-input-group">
                        <label>Email Address</label>
                        <input
                            type="email"
                            className="superadmin-input"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isLoading}
                            required
                        />
                    </div>

                    <div className="superadmin-input-group">
                        <label>Password</label>
                        <input
                            type="password"
                            className="superadmin-input"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={isLoading}
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        className="superadmin-btn"
                        disabled={isLoading || formMessage.type === 'success'}
                    >
                        {isLoading ? 'Authenticating...' : 'Login Securely'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default SuperAdminLogin;
